import { Fractions, notEmpty } from '@ezchops/helpers';
import { isEqual, range } from 'lodash';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import { isChordTuplet } from '../sheet';
import { withDots } from './sheetToGrid';
export function sheetToAbc(sheet, useRepeats = true, stave) {
    const denominators = sheet.segments.flatMap(segmentDenominators);
    const abcTick = Math.max(...denominators);
    const segmentsAbc = sheet.segments
        .map((segment, i, segments) => {
        const isFirst = i == 0;
        const isLast = i == segments.length - 1;
        const insertSignature = !isFirst &&
            !isEqual(segments[i - 1].signature, segment.signature) &&
            (stave == null || stave == 'hands');
        const voice = segmentVoice(segment, abcTick, useRepeats, insertSignature, stave);
        if (segment.repeats == null) {
            return `["∞ x" ${voice} :`;
        }
        else if (segment.repeats == 0) {
            return voice;
        }
        else if (useRepeats && segment.repeats != null) {
            return `[${segment.repeats + 1}  ${voice} :`;
        }
        else if (segment.repeats != null) {
            return range(0, segment.repeats + 1).map(() => voice).join(' | ');
        }
    });
    return [`${segmentsAbc.join(' | ')}|]`, abcTick];
}
export function segmentToAbc(segment, unitNote = null, useRepeats = true, insertSignature = false, stave) {
    const abcTick = unitNote ?? Math.max(...segmentDenominators(segment));
    const voice = segmentVoice(segment, abcTick, useRepeats, insertSignature, stave);
    return [voice, abcTick];
}
function segmentDenominators(segment) {
    return segment.measures
        .flatMap(({ notes }) => notes.map(note => {
        const [, denominator] = !isChordTuplet(note) && (note.dots ?? 0) > 0
            ? withDots(note.duration, note.dots ?? 0)
            : note.duration;
        return denominator;
    }));
}
function segmentVoice(segment, abcTick, useMeasureRepeats = true, insertSignature = false, stave) {
    const { measureVoices } = segment.measures.reduce(foldMeasureVoice, { measureVoices: [] });
    return `${measureVoices.join(' |\\ \n')}`;
    function foldMeasureVoice(agg, measure) {
        const measureBeats = foldBeats(measure, segment.signature);
        const beatVoices = measureBeats.map(notes => {
            const beatAbc = notes.map(note => isChordTuplet(note)
                ? note.chords.every(chord => chord.length == 1 && chord[0].pitch == null)
                    ? tupletImproVoice(note)
                    : tupletVoice(note)
                : chordVoice(note));
            const beatVoice = beatAbc.map(({ beatVoice }) => beatVoice).join('');
            const beatPrefix = uniq(beatAbc.flatMap(({ beatPrefix }) => beatPrefix)).join('');
            return `${beatPrefix}${beatVoice}`;
        });
        const signaturePrefix = insertSignature && (stave == null || stave == 'hands')
            ? `M:${segment.signature.numerator}/${segment.signature.denominator} `
            : '';
        const measureVoice = `${signaturePrefix}${beatVoices.join(' ')}`;
        const repeatPrefix = isEqual(agg.lastMeasure, measure) && useMeasureRepeats
            ? '%%repeat 1 1\n'
            : '';
        return {
            measureVoices: [...agg.measureVoices, `${repeatPrefix}${measureVoice}`],
            lastMeasure: measure
        };
    }
    function tupletImproVoice(tuplet) {
        const [, denominator] = tuplet.duration;
        const durationPostfix = voiceLengthPostfix(1, denominator, abcTick);
        const chordAbc = chordToAbc(stave, ...tuplet.chords[0]);
        return {
            beatVoice: `${chordAbcToVoice(chordAbc)}${durationPostfix}`,
            beatPrefix: chordAbc.beatPrefix
        };
    }
    function tupletVoice(tuplet) {
        const [, denominator] = tuplet.duration;
        const durationPostfix = voiceLengthPostfix(1, denominator, abcTick);
        const tupletPrefix = `(${tuplet.chords.length}`;
        const chordAbcs = tuplet.chords.map(chord => chordToAbc(stave, ...chord));
        const beatPrefix = uniq(chordAbcs.flatMap(({ beatPrefix }) => beatPrefix));
        const chords = chordAbcs
            .map((chordAbc) => `${chordAbcToVoice(chordAbc)}${durationPostfix}`)
            .join('');
        return {
            beatVoice: `${tupletPrefix}${chords}`,
            beatPrefix
        };
    }
    function chordVoice(chord) {
        const [numerator, denominator] = (chord.dots ?? 0) > 0
            ? withDots(chord.duration, chord.dots ?? 0)
            : chord.duration;
        const durationPostfix = voiceLengthPostfix(numerator, denominator, abcTick);
        const chordAbc = chordToAbc(stave, ...chord.notes);
        return {
            beatVoice: `${chordAbcToVoice(chordAbc)}${durationPostfix}`,
            beatPrefix: chordAbc.beatPrefix
        };
    }
}
function chordAbcToVoice(chordAbc) {
    const nonNullNotes = chordAbc.notes.filter(n => n.noteAbc != null);
    const noteVoices = nonNullNotes.length > 0
        ? nonNullNotes.map(({ notePrefix, noteAbc }) => `${notePrefix}${noteAbc}`).join('')
        : 'x';
    const chordPrefix = chordAbc.chordPrefix.join('');
    const chordVoice = nonNullNotes.length > 1 ? `[${noteVoices}]` : noteVoices;
    return `${chordPrefix}${chordVoice}`;
}
function foldBeats(measure, signature) {
    const { denominator } = signature;
    const commonDenominator = Fractions.commonDenominator(...measure.notes.map(({ duration }) => duration));
    const beatTicks = commonDenominator / denominator;
    const { beats, beat } = measure.notes.reduce((agg, note) => {
        const noteTicks = note.duration[0] * commonDenominator / note.duration[1];
        const beat = [...agg.beat, note];
        if (agg.ticks + noteTicks >= beatTicks) {
            return { beats: [...agg.beats, beat], beat: [], ticks: 0 };
        }
        else {
            return { beats: agg.beats, beat, ticks: agg.ticks + noteTicks };
        }
    }, { beats: [], beat: [], ticks: 0 });
    return beat.length > 0 ? [...beats, beat] : beats;
}
function voiceLengthPostfix(numerator, denominator, abcTick) {
    const abcTicks = abcTick * numerator / denominator;
    return abcTicks > 1
        ? abcTicks.toString()
        : ''.padStart(Math.log2(1 / abcTicks), '/');
}
function chordToAbc(stave, ...hits) {
    if (hits.length == 0) {
        return {
            notes: [{ noteAbc: 'z', notePrefix: [] }],
            chordPrefix: [],
            beatPrefix: []
        };
    }
    const hitAbcs = sortBy(hits, 'pitch').map(h => hitToAbc(h, stave));
    const chordPrefix = uniq(hitAbcs.flatMap(({ chordPrefix }) => chordPrefix)).filter(notEmpty);
    const beatPrefix = uniq(hitAbcs.flatMap(({ beatPrefix }) => beatPrefix)).filter(notEmpty);
    return {
        notes: hitAbcs.map(({ prefix, abc }) => ({ notePrefix: prefix, noteAbc: abc })),
        chordPrefix,
        beatPrefix: beatPrefix
    };
}
function hitToAbc(hit, stave) {
    if (stave == 'sticking') {
        const sticking = [hit.sticking].filter(notEmpty).map(s => `"${s}"`);
        return {
            abc: null, prefix: [], chordPrefix: sticking, beatPrefix: [],
        };
    }
    const pad = hit.pitch == null ? null : hit.pitch;
    const abc = (stave == undefined) || (isFeet(hit) && stave == 'feet') || (isHands(hit) && stave == 'hands')
        ? padAbc(pad)
        : null;
    const chordPrefix = [
        hit.dynamics == 'accent' ? '!accent!' : null
    ].filter(notEmpty);
    const prefix = [
        hit.dynamics == 'ghost' ? '!ghost!' : null
    ].filter(notEmpty);
    if (pad == null) {
        return {
            abc, prefix, chordPrefix, beatPrefix: ['">Impr."'],
        };
    }
    else if (pad == Pad.OpenHiHat) {
        return {
            abc, prefix, chordPrefix: ['!open!', ...chordPrefix], beatPrefix: []
        };
    }
    else {
        return {
            abc, prefix, chordPrefix, beatPrefix: []
        };
    }
}
function padAbc(pad) {
    if (pad == null) {
        return 'x';
    }
    else if (pad == Pad.OpenHiHat) {
        return AbcNote[Pad.ClosedHiHat];
    }
    else if (pad in AbcNote) {
        return AbcNote[pad];
    }
    else {
        return null;
    }
}
function isHands(hit) {
    return !isFeet(hit);
}
function isFeet(hit) {
    return hit.pitch == Pad.Kick || hit.pitch == Pad.PedalHiHat;
}
export var Pad;
(function (Pad) {
    Pad[Pad["Crash"] = 49] = "Crash";
    Pad[Pad["Crash2"] = 57] = "Crash2";
    Pad[Pad["Ride"] = 51] = "Ride";
    Pad[Pad["RideBell"] = 53] = "RideBell";
    Pad[Pad["OpenHiHat"] = 46] = "OpenHiHat";
    Pad[Pad["ClosedHiHat"] = 42] = "ClosedHiHat";
    Pad[Pad["PedalHiHat"] = 44] = "PedalHiHat";
    Pad[Pad["AcousticSnare"] = 38] = "AcousticSnare";
    Pad[Pad["Snare"] = 40] = "Snare";
    Pad[Pad["HighTom"] = 48] = "HighTom";
    Pad[Pad["LowTom"] = 45] = "LowTom";
    Pad[Pad["FloorTom"] = 43] = "FloorTom";
    Pad[Pad["Kick"] = 36] = "Kick";
    Pad[Pad["AcousticKick"] = 35] = "AcousticKick";
})(Pad || (Pad = {}));
export const AbcNote = {
    [Pad.Crash]: '^c\'',
    [Pad.Crash2]: '^c\'',
    [Pad.Ride]: '^A\'',
    [Pad.RideBell]: '^B\'',
    // open !open!
    [Pad.OpenHiHat]: '!open!^g',
    [Pad.ClosedHiHat]: '^g',
    // buzzroll !///!
    // ghost !(.!!).!
    // accent !accent!
    // flam {/c}
    [Pad.AcousticSnare]: 'c',
    [Pad.Snare]: 'c',
    [Pad.HighTom]: 'e',
    [Pad.LowTom]: 'd',
    [Pad.FloorTom]: 'A',
    [Pad.PedalHiHat]: '^d,',
    [Pad.Kick]: 'F',
    [Pad.AcousticKick]: 'F'
};
//# sourceMappingURL=sheetToAbc.js.map